<script setup lang="ts">
import { onMounted, watch } from 'vue';
import { todaycode, transformCodeToDate } from '@/functions/utils';
import { filteredCulturesRecolte } from '@/functions/functions_cultures';
import {
  resetNewRecolte,
  addBatch,
  deleteRecolte,
  editRecolte,
  handleEditSubmit,
  useRecolteComputed,
  handleSubmit,
  toggleRecolte,
  getAnneesDisponibles,
  getMoisDisponibles,
  filteredRecoltes,
  deleteBatch,
} from '@/functions/functions_recoltes';
import { getObservationValue } from '@/functions/functions_prelevements';
import { isRecolteSpecieMi175, isRecolteSpecieMi136 } from '@/functions/functions_recoltes';
import { useConfirm } from '@/composables/useConfirm';
import ConfirmModal from '@/components/ConfirmModal.vue';
import { useI18n } from 'vue-i18n';

const { t } = useI18n();
const { confirm } = useConfirm();

const mois = getMoisDisponibles(t);
const annees = getAnneesDisponibles();

async function handleDeleteRecolte(id: number) {
  const accepted = await confirm(t('deleteRecolte'));
  if (accepted) {
    await deleteRecolte(id, t);
  }
}

const allowedObservationTypeIdsMi136 = [7, 10];
const allowedObservationTypeIdsMi175 = [1, 2, 7]; // Champignon (2) -> +

const shouldDisplayObservation = (id: number, specie: number) => {
  if (isRecolteSpecieMi175(specie)) return allowedObservationTypeIdsMi175.includes(id);
  if (isRecolteSpecieMi136(specie)) return allowedObservationTypeIdsMi136.includes(id);
  return undefined
};

async function handleDeleteBatch(id: number) {
  const accepted = await confirm(t('deleteBatch'));
  if (accepted) {
    await deleteBatch(id, t);
  }
}

import { useDataStore } from '@/stores/useDataStore';
const dataStore = useDataStore();

// Function to calculate the average observation values for a given harvest
const calculateObservationAverages = (cultures: any[], observationTypes: any[], specieId: number) => {
  const averages: { [key: number]: number } = {};

  observationTypes.forEach((type) => {
    if (shouldDisplayObservation(type.id, specieId)) {
      let sum = 0;
      let count = 0;

      cultures.forEach((culture) => {
        if (culture.prelevement) { // Check if prelevement exists
          const value = getObservationValue(culture.prelevement, type.id);
          if (value !== null && value !== undefined) {
            // Multiply by 20 for Champignon (type ID 2)
            const adjustedValue = type.id === 2 ? value * 20 : value;
            sum += adjustedValue;
            count++;
          }
        }
      });

      // Calculate average
      averages[type.id] = count > 0 ? Math.round((sum / count) / 0.5) * 0.5 : 0;
    }
  });

  return averages;
};

onMounted(async () => {
  await dataStore.loadBasicData();
  dataStore.selectedCulture = null;
  dataStore.showInactive = false;
  dataStore.showCreateRecolte = false;
  dataStore.filtreEspece = null;
  dataStore.filtreAnnee = new Date().getFullYear();
  dataStore.filtreMois = new Date().getMonth() + 1;
  resetNewRecolte();
  await fetchLightCultures();
});

// Surveiller les changements de filtres
watch(
  () => [dataStore.filtreAnnee, dataStore.filtreMois, dataStore.filtreEspece],
  async ([newAnnee, newMois, newSpecie]) => {
    if (newAnnee === null && newMois !== null) {
      dataStore.filtreMois = null;
      return;
    }
    dataStore.isDataLoading = true;
    await fetchRecoltes(newAnnee, newMois, newSpecie);
    dataStore.isDataLoading = false;
  },
);

const {
  isNoCultureSelected,
  selectedSpecie,
  selectedPBRs
} = useRecolteComputed();

import { useAuthStore } from '@/stores/useAuthStore';
import { fetchLightCultures, fetchRecoltes } from '@/functions/fetch_tables';
import Maintenance from '@/components/Maintenance.vue';
const authStore = useAuthStore();
</script>

<template>
  <ConfirmModal />
  <Maintenance />
  <div v-if="authStore.isAuthenticated && authStore.scopes.includes('reader')">
    <div>
      <h1>{{ $t('harvestTitle') }}</h1>
    </div>

    <div v-if="dataStore.isDataLoading" class="spinner-container module">
      <div class="spinner"></div>
      <p>{{ $t('loadingData') }}</p>
    </div>

    <div v-if="!dataStore.isDataLoading && authStore.scopes.includes('editor')" class="module"
      style="padding-top: 25px;">
      <div v-if="!dataStore.showCreateRecolte">
        <div class="button-group">
          <button @click="resetNewRecolte(); dataStore.showCreateRecolte = true">
            + {{ $t('newHarvestButton') }}
          </button>
        </div>
      </div>

      <div v-if="dataStore.showCreateRecolte">
        <div>
          <p>{{ $t('selectCultures') }}</p>
          <div class="cultures-container">
            <label v-for="culture in filteredCulturesRecolte" class="culture-item" :key="culture.id">
              <input v-model="dataStore.selectedCultures" type="checkbox" class="styled-checkbox" :class="{
                active: dataStore.selectedCultures.includes(culture.id),
                off: !culture.active,
                on: culture.active
              }" :value="culture.id" />
              {{ culture.pbr.numero_pbr }} | {{ culture.specie.specie_code }} | {{ culture.startdaycode.startday_code }}
            </label>
          </div>
        </div>

        <!-- Affichage des erreurs -->
        <div
          v-if="dataStore.formErrorMessage || dataStore.creationError || isNoCultureSelected || selectedSpecie === null"
          style="color: red; margin-bottom: 1rem; width: 100%;">
          <p v-if="selectedSpecie === null && !isNoCultureSelected">{{ $t('errorDifferentSpecies') }}</p>
          <p v-if="dataStore.formErrorMessage">{{ dataStore.formErrorMessage }}</p>
          <p v-if="dataStore.creationError">{{ dataStore.creationError }}</p>
        </div>

        <!-- Formulaire de création -->
        <form class="form-recolte" @submit.prevent="handleSubmit(t)">
          <input v-if="dataStore.newRecolte" v-model="dataStore.newRecolte.daycode" pattern="^\d{2}-\d{3}$"
            class="form-input" :title="$t('startdayCodeInvalid')" :placeholder="todaycode" />

          <!-- Séléction de la Machine -->
          <select v-if="dataStore.newRecolte" v-model="dataStore.newRecolte.machine_id" class="styled-dropdown">
            <option :value="null" disabled selected>{{ $t('selectMachine') }}</option>
            <option v-for="machine in dataStore.machines" :value="machine.id" :key="machine.id">
              {{ machine.machine_name }}
            </option>
          </select>

          <!-- Espèce automatiquement sélectionnée en fonction des cultures -->
          <div style="display: flex; flex-wrap: wrap; gap: 1rem;">
            <label
              style="display: flex; align-items: center; background: #f5f5f5; padding: 0.5rem 1rem; border-radius: 8px;">{{
                $t('specie') }}:
              {{ selectedSpecie?.specie_code }}
            </label>
          </div>

          <!-- PBRs automatiquement sélectionnés en fonction des cultures -->
          <div style="display: flex; flex-wrap: wrap; gap: 1rem;">
            <label v-if="selectedPBRs.length"
              style="display: flex; align-items: center; background: #f5f5f5; padding: 0.5rem 1rem; border-radius: 8px;">
              PBRs : {{selectedPBRs.map(p => p.numero_pbr).join(', ')}}
            </label>
          </div>

          <!-- Volume centrifugé -->
          <label for="volume">{{ $t('centrifugedVolume') }} (L)</label>
          <input v-if="dataStore.newRecolte" v-model.number="dataStore.newRecolte.volume" required class="form-input" />

          <div class="button-group">
            <button type="submit">{{ $t('create') }}</button>
            <button type="button" @click="dataStore.showCreateRecolte = false">{{ $t('cancel') }}</button>
          </div>
        </form>
      </div>
    </div>

    <div v-if="!dataStore.isDataLoading" class="module-recoltes">

      <!-- Filtres -->
      <div class="dropdown-container">
        <label>
          {{ $t('species') }}
          <select v-model="dataStore.filtreEspece" class="styled-dropdown">
            <option :value="null">{{ $t('allSpeciesOption') }}</option>
            <option v-for="specie in dataStore.species" :value="specie.id" :key="specie.id">
              {{ specie.specie_code }}
            </option>
          </select>
        </label>

        <label>
          {{ $t('yearLabel') }}
          <select v-model="dataStore.filtreAnnee" class="styled-dropdown">
            <option :value="null">{{ $t('allYearsOption') }}</option>
            <option v-for="annee in annees" :value="annee.value" :key="annee.value">{{ annee.value }}</option>
          </select>
        </label>

        <label>
          {{ $t('monthLabel') }}
          <select v-model="dataStore.filtreMois" class="styled-dropdown">
            <option :value="null">{{ $t('allMonthsOption') }}</option>
            <option v-for="m in mois" :value="m.value" :key="m.value">{{ m.label }}</option>
          </select>
        </label>
      </div>

      <div v-if="dataStore.recoltes.length === 0">
        <h3>{{ $t('noHarvest') }}</h3>
      </div>

      <div v-else>
        <h2>{{ $t('harvestBisTitle') }}</h2>

        <table class="table-recoltes">
          <thead>
            <tr>
              <th class="recoltes small">{{ $t('date') }}</th>
              <th class="recoltes small">{{ $t('specie') }}</th>
              <th class="recoltes small">Machine</th>
              <th class="recoltes small">PBRs</th>
              <th class="recoltes small">{{ $t('numberOfBatch') }}</th>
              <th class="recoltes small">{{ $t('centrifugedVolume') }}</th>
              <th class="recoltes middle"></th>
            </tr>
          </thead>
          <tbody v-for="recolte in filteredRecoltes" :key="recolte.id">
            <!-- Ligne principale -->
            <tr>
              <td>{{ transformCodeToDate(recolte.daycode) }}</td>
              <td>{{ recolte.specie?.specie_code || 'Non définie' }}</td>
              <td>{{ recolte.machine?.machine_name || 'Non définie' }}</td>
              <td>
                <template v-if="recolte.cultures?.length">
                  {{[...new Set(recolte.cultures.map(c => c.pbr?.numero_pbr).filter(p => p))].join(', ')}}
                </template>
              </td>
              <td>{{ recolte.batches?.length || 0 }}</td>
              <td>{{ recolte.volume.toFixed(0) }} L</td>
              <td>
                <button class="custom-btn" @click="toggleRecolte(recolte.id)">
                  <span v-html="dataStore.selectedRecolteForBatch?.id === recolte.id ? '&#9650;' : '&#9660;'"></span>
                </button>
                <button v-if="authStore.scopes.includes('editor')" class="custom-btn"
                  @click="editRecolte(recolte)">✏️</button>
                <button v-if="authStore.scopes.includes('editor')" class="custom-btn"
                  @click="handleDeleteRecolte(recolte.id)">&#10060;</button>
              </td>
            </tr>

            <!-- Formulaire de modification de la récolte -->
            <tr v-if="dataStore.showEditRecolte && dataStore.selectedRecolteForEdit?.id === recolte.id">
              <td colspan="2" class="form-recolte">
                <div style="flex: 2; min-width: 300px;">
                  <h3>{{ $t('editHarvest') }}</h3>
                  <form @submit.prevent="handleEditSubmit(t)">
                    <input v-model="dataStore.selectedRecolteForEdit.daycode" pattern="^\d{2}-\d{3}$" class="form-input"
                      required />

                    <select v-model="dataStore.selectedRecolteForEdit.machine.id" class="styled-dropdown">
                      <option :value="null" disabled selected>{{ $t('selectMachine') }}</option>
                      <option v-for="machine in dataStore.machines" :value="machine.id" :key="machine.id">
                        {{ machine.machine_name }}
                      </option>
                    </select>

                    <label for="volume">{{ $t('centrifugedVolume') }} (L)</label>
                    <input v-model.number="dataStore.selectedRecolteForEdit.volume" class="form-input" required />

                    <div class="button-group">
                      <button type="submit">{{ $t('update') }}</button>
                      <button type="button" @click="dataStore.showEditRecolte = false">{{ $t('cancel') }}</button>
                    </div>
                  </form>
                </div>
              </td>
            </tr>

            <!-- Ligne de détails déroulable -->
            <tr v-if="dataStore.selectedRecolteForBatch?.id === recolte.id">
              <td colspan="6" class="form-recolte">
                <div style="display: flex; gap: 20px;">
                  <!-- Liste des batches si elle existe et n'est pas vide -->
                  <div v-if="recolte.batches && recolte.batches.length > 0" style="flex: 2;">
                    <h4>{{ $t('associatedBatches') }}</h4>
                    <table class="table-recoltes">
                      <thead>
                        <tr>
                          <th class="recoltes small">{{ $t('batchCode') }}</th>
                          <th class="recoltes middle">{{ $t('gelQuantity') }}</th>
                          <th class="recoltes small"></th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="(batch, idx) in recolte.batches" :key="idx">
                          <td>{{ batch.batch_code || 'N/A' }}</td>
                          <td>{{ batch.quantity }} Kg</td>
                          <td>
                            <button v-if="authStore.scopes.includes('editor')" class="custom-btn"
                              @click="handleDeleteBatch(batch.id)">&#10060;
                            </button>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>

                  <!-- Affichage quand il n'y a pas de batches -->
                  <div v-else style="flex: 2;">
                    <h4>{{ $t('associatedBatches') }}</h4>
                    <table class="table-recoltes">
                      <thead>
                        <tr>
                          <th class="recoltes small">{{ $t('noBatchForRecolte') }}</th>
                        </tr>
                      </thead>
                    </table>
                  </div>

                  <!-- Affichage des Observations des cultures -->
                  <div style="flex: 2;">
                    <h4 v-show="isRecolteSpecieMi175(recolte.specie.id) || isRecolteSpecieMi136(recolte.specie.id)">
                      Observations
                    </h4>
                    <table class="table-recoltes">
                      <thead>
                        <tr>
                          <th v-for="type in dataStore.observationTypes"
                            v-show="shouldDisplayObservation(type.id, recolte.specie.id)" class="recoltes small"
                            :key="type.id">
                            {{ type.name }}
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        <!-- Average row -->
                        <tr v-show="isRecolteSpecieMi175(recolte.specie.id) || isRecolteSpecieMi136(recolte.specie.id)">
                          <td v-for="type in dataStore.observationTypes"
                            v-show="shouldDisplayObservation(type.id, recolte.specie.id)" :key="type.id">
                            <strong>{{ calculateObservationAverages(recolte.cultures, dataStore.observationTypes,
                              recolte.specie.id)[type.id] + ' %' }}</strong>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>

                  <!-- Formulaire d'ajout -->
                  <div v-if="authStore.scopes.includes('editor')" style="flex: 1; min-width: 300px;">
                    <h4>{{ $t('newBatchButton') }}</h4>
                    <form @submit.prevent="addBatch(t)">
                      <label>
                        {{ $t('gelQuantity') }} (Kg):
                        <input v-model="dataStore.newBatch.quantity" type="number" required />
                      </label>
                      <label>
                        <input v-model="dataStore.newBatch.rec" type="checkbox" class="styled-checkbox" />
                        {{ $t('recycledEnvironment') }}
                      </label>
                      <div class="button-group">
                        <button type="submit">{{ $t('addBatchButton') }}</button>
                      </div>
                    </form>
                  </div>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
  <div v-else class="centered-block">
    <h2>{{ $t('loginRequiredTitle') }}</h2>
    <p>{{ $t('loginRequiredMessage') }}</p>
  </div>
</template>